import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError } from '@memberjunction/core';
import {
    marketingBlogEntity,
    marketingBlogRevisionEntity,
    marketingContentStatusEntity,
    marketingNewsletterEntity,
    marketingSocialPostEntity,
} from 'mj_generatedentities';
import { ROLE_RANK, RoleTier } from '../../marketing-workspace.routes';

type ApprovalsFilter = 'needs-review' | 'scheduled' | 'all' | 'newsletters' | 'social-posts';

/**
 * Foundation / Approvals — review queue with KPI strip + filter pills + side panel.
 * Mirrors the AI CMO review queue UX. Composes existing <mkt-review-queue> for blogs.
 *
 * State flows down from the workspace shell — blogs/social/newsletters/contentStatuses are inputs.
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-foundation-approvals',
    template: `
<mkt-ws-page-head
    [eyebrow]="'Foundation · Approvals' + (companyName ? ' · ' + companyName : '')"
    title="Review queue."
    sub="Drafts pending CMO review across all channels. Open one to read, edit, approve, or reject.">
</mkt-ws-page-head>

<div *ngIf="isLoadingContent" class="ap__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading…</div>

<ng-container *ngIf="!isLoadingContent">
    <!-- KPI bar — totals across all content types -->
    <div class="ap__kpis">
        <div class="ap__kpi" [class.ap__kpi--alert]="needsReviewCount > 0" (click)="setFilter('needs-review')">
            <div class="ap__kpi-value">{{ needsReviewCount }}</div>
            <div class="ap__kpi-label">Needs review</div>
        </div>
        <div class="ap__kpi" (click)="setFilter('scheduled')">
            <div class="ap__kpi-value">{{ scheduledCount }}</div>
            <div class="ap__kpi-label">Ready / scheduled</div>
        </div>
        <div class="ap__kpi" (click)="setFilter('all')">
            <div class="ap__kpi-value">{{ blogs.length }}</div>
            <div class="ap__kpi-label">Total blogs</div>
        </div>
        <div class="ap__kpi" (click)="setFilter('social-posts')">
            <div class="ap__kpi-value">{{ socialPosts.length }}</div>
            <div class="ap__kpi-label">Social posts</div>
        </div>
        <div class="ap__kpi" (click)="setFilter('newsletters')">
            <div class="ap__kpi-value">{{ newsletters.length }}</div>
            <div class="ap__kpi-label">Newsletters</div>
        </div>
    </div>

    <!-- Filter pills -->
    <div class="ap__pills">
        <button class="ap__pill" [class.ap__pill--active]="filter === 'needs-review'" (click)="setFilter('needs-review')">
            Needs review · {{ needsReviewCount }}
        </button>
        <button class="ap__pill" [class.ap__pill--active]="filter === 'scheduled'" (click)="setFilter('scheduled')">
            Ready to publish · {{ scheduledCount }}
        </button>
        <button class="ap__pill" [class.ap__pill--active]="filter === 'all'" (click)="setFilter('all')">
            All blogs · {{ blogs.length }}
        </button>
        <button class="ap__pill" [class.ap__pill--active]="filter === 'social-posts'" (click)="setFilter('social-posts')">
            <i class="fa-solid fa-share-nodes"></i> Social · {{ socialPosts.length }}
        </button>
        <button class="ap__pill" [class.ap__pill--active]="filter === 'newsletters'" (click)="setFilter('newsletters')">
            <i class="fa-solid fa-envelope"></i> Newsletters · {{ newsletters.length }}
        </button>
    </div>

    <!-- Blog filters use the existing review-queue component (with side panel built in) -->
    <ng-container *ngIf="filter === 'needs-review' || filter === 'scheduled' || filter === 'all'">
        <mkt-review-queue
            *ngIf="filteredBlogs.length > 0; else emptyBlogs"
            [blogs]="filteredBlogs"
            [selectedBlogId]="selectedBlogId"
            [selectedBlog]="selectedBlog"
            [allRevisions]="revisions"
            [brandVoiceScores]="brandVoiceScores"
            (selectBlog)="onSelectBlog($event)"
            (closeBlogDetail)="onCloseBlogDetail()"
            (statusChange)="onStatusChange($event)"
            (deleteBlog)="onDeleteBlog($event)"
            (blogSaved)="onBlogSaved()">
        </mkt-review-queue>
        <ng-template #emptyBlogs>
            <mkt-ws-coming-soon
                icon="fa-circle-check"
                title="All caught up"
                body="Nothing in the {{ filter === 'all' ? 'queue' : filter.replace('-', ' ') }}.">
            </mkt-ws-coming-soon>
        </ng-template>
    </ng-container>

    <!-- Social posts list (light) -->
    <div *ngIf="filter === 'social-posts'" class="ap__simple-list">
        <mkt-ws-coming-soon
            *ngIf="socialPosts.length === 0"
            icon="fa-share-nodes" title="No social drafts" body="Social posts will appear here when drafted.">
        </mkt-ws-coming-soon>
        <div *ngFor="let sp of socialPosts" class="ap__simple-row">
            <mkt-ws-pill [variant]="statusToVariant(getStatusName(sp.Get('ContentStatusID')))">{{ getStatusName(sp.Get('ContentStatusID')) }}</mkt-ws-pill>
            <div class="ap__simple-title">{{ socialPostTitle(sp) }}</div>
            <div class="ap__simple-meta">{{ sp.Get('SocialPlatform') || '—' }}</div>
        </div>
    </div>

    <!-- Newsletters list -->
    <div *ngIf="filter === 'newsletters'" class="ap__simple-list">
        <mkt-ws-coming-soon
            *ngIf="newsletters.length === 0"
            icon="fa-envelope" title="No newsletters" body="Newsletter drafts will appear here.">
        </mkt-ws-coming-soon>
        <div *ngFor="let n of newsletters" class="ap__simple-row">
            <mkt-ws-pill [variant]="statusToVariant(getStatusName(n.Get('ContentStatusID')))">{{ getStatusName(n.Get('ContentStatusID')) }}</mkt-ws-pill>
            <div class="ap__simple-title">{{ newsletterTitle(n) }}</div>
        </div>
    </div>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .ap__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }

        .ap__kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
        .ap__kpi { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 14px 16px; cursor: pointer; transition: border-color 0.15s; }
        .ap__kpi:hover { border-color: var(--mj-text-primary, #1a1a1a); }
        .ap__kpi--alert .ap__kpi-value { color: var(--mj-status-warning, #b45309); }
        .ap__kpi-value { font-size: 26px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); font-variant-numeric: tabular-nums; line-height: 1.1; }
        .ap__kpi-label { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); margin-top: 6px; }

        .ap__pills { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .ap__pill { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 99px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-secondary, #71717a); font-size: 12px; font-weight: 500; font-family: inherit; cursor: pointer; transition: background 0.15s, color 0.15s, border-color 0.15s; }
        .ap__pill:hover { color: var(--mj-text-primary, #1a1a1a); }
        .ap__pill--active { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border-color: var(--mj-brand-primary, #6366f1); }
        .ap__pill i { font-size: 11px; }

        .ap__simple-list { display: flex; flex-direction: column; gap: 6px; }
        .ap__simple-row { display: flex; align-items: center; gap: 14px; padding: 10px 14px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; }
        .ap__simple-title { flex: 1; font-size: 13px; color: var(--mj-text-primary, #1a1a1a); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ap__simple-meta { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }

        @media (max-width: 1100px) { .ap__kpis { grid-template-columns: repeat(2, 1fr); } }
    `]
})
export class FoundationApprovalsSection {
    @Input() companyId = '';
    @Input() companyName = '';
    @Input() blogs: marketingBlogEntity[] = [];
    @Input() socialPosts: marketingSocialPostEntity[] = [];
    @Input() newsletters: marketingNewsletterEntity[] = [];
    @Input() contentStatuses: marketingContentStatusEntity[] = [];
    @Input() isLoadingContent = false;
    @Input() blogStatuses: unknown[] = [];
    @Input() socialPlatforms: unknown[] = [];
    @Input() channelConfigs: unknown[] = [];
    @Input() brandConfig: unknown = null;
    @Input() recommendations: unknown[] = [];
    @Input() navigate: (path: string) => void = () => {};
    /**
     * Active user's marketing role for the selected company. Null = system /
     * Owner-type user (full access). Used to belt-and-suspenders gate
     * approve/reject/delete handlers below — the route is already
     * `requiredRole: 'Operator'`, so under-privileged users normally never
     * reach this section, but if the section is ever embedded elsewhere or
     * deep-linked under a different gate, the handlers themselves no-op for
     * Viewers. (#938)
     */
    @Input() userRole: RoleTier | null = null;

    private cdr = inject(ChangeDetectorRef);

    filter: ApprovalsFilter = 'needs-review';
    selectedBlogId: string | null = null;
    selectedBlog: marketingBlogEntity | null = null;
    revisions: marketingBlogRevisionEntity[] = [];
    brandVoiceScores: Record<string, number> = {};

    // Computed counts ─────────────────────────────────
    get needsReviewCount(): number {
        return this.blogs.filter(b => this.isNeedsReview(b)).length;
    }
    get scheduledCount(): number {
        return this.blogs.filter(b => this.isScheduled(b)).length;
    }
    get filteredBlogs(): marketingBlogEntity[] {
        switch (this.filter) {
            case 'needs-review': return this.blogs.filter(b => this.isNeedsReview(b));
            case 'scheduled': return this.blogs.filter(b => this.isScheduled(b));
            case 'all': return this.blogs;
            default: return [];
        }
    }

    /** True if the active user has Operator+ rank (or is a system user). */
    get canWrite(): boolean {
        if (this.userRole === null) return true;
        return ROLE_RANK[this.userRole] >= ROLE_RANK['Operator'];
    }

    setFilter(f: ApprovalsFilter): void {
        this.filter = f;
        this.selectedBlogId = null;
        this.selectedBlog = null;
        this.cdr.detectChanges();
    }

    onSelectBlog(blogId: string): void {
        this.selectedBlogId = blogId;
        this.selectedBlog = this.blogs.find(b => b.ID === blogId) ?? null;
        this.cdr.detectChanges();
    }

    onCloseBlogDetail(): void {
        this.selectedBlogId = null;
        this.selectedBlog = null;
        this.cdr.detectChanges();
    }

    async onStatusChange(event: { blogId: string; newStatus: string }): Promise<void> {
        if (!this.canWrite) {
            LogError(`[FoundationApprovals] onStatusChange blocked: role '${this.userRole}' lacks Operator rank`);
            return;
        }
        const blog = this.blogs.find(b => b.ID === event.blogId);
        if (!blog) return;
        const status = this.contentStatuses.find(s => s.Name === event.newStatus);
        if (!status) {
            LogError(`[FoundationApprovals] Unknown content status '${event.newStatus}'`);
            return;
        }
        try {
            blog.Set('ContentStatusID', status.ID);
            const saved = await blog.Save();
            if (!saved) {
                LogError(`[FoundationApprovals] blog.Save() failed for blog ${blog.ID}: ${blog.LatestResult?.Message ?? 'unknown'}`);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[FoundationApprovals] onStatusChange threw for blog ${event.blogId}: ${msg}`);
        } finally {
            this.cdr.detectChanges();
        }
    }

    async onDeleteBlog(blogId: string): Promise<void> {
        if (!this.canWrite) {
            LogError(`[FoundationApprovals] onDeleteBlog blocked: role '${this.userRole}' lacks Operator rank`);
            return;
        }
        const blog = this.blogs.find(b => b.ID === blogId);
        if (!blog) return;
        try {
            if (await blog.Delete()) {
                this.onCloseBlogDetail();
            } else {
                LogError(`[FoundationApprovals] blog.Delete() failed for blog ${blogId}: ${blog.LatestResult?.Message ?? 'unknown'}`);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[FoundationApprovals] onDeleteBlog threw for blog ${blogId}: ${msg}`);
        }
    }

    onBlogSaved(): void {
        this.cdr.detectChanges();
    }

    getStatusName(id: unknown): string {
        const s = this.contentStatuses.find(cs => cs.ID === id);
        return s?.Name ?? '—';
    }

    socialPostTitle(sp: marketingSocialPostEntity): string {
        const content = String(sp.Get('Content') ?? '').slice(0, 80);
        return content || 'Untitled social post';
    }

    newsletterTitle(n: marketingNewsletterEntity): string {
        const title = String(n.Get('Subject') ?? n.Get('Title') ?? '');
        return title || 'Untitled newsletter';
    }

    statusToVariant(status: string): 'good' | 'warn' | 'bad' | 'info' | 'muted' {
        const s = status.toLowerCase();
        if (s.includes('publish')) return 'good';
        if (s.includes('approved') || s.includes('ready') || s.includes('scheduled')) return 'good';
        if (s.includes('review') || s.includes('draft')) return 'warn';
        if (s.includes('reject')) return 'bad';
        return 'muted';
    }

    private isNeedsReview(b: marketingBlogEntity): boolean {
        const status = String(b.Get('Status') ?? '').toLowerCase();
        return status === 'draft' || status === 'review';
    }
    private isScheduled(b: marketingBlogEntity): boolean {
        const status = String(b.Get('Status') ?? '').toLowerCase();
        return status === 'ready' || status === 'scheduled' || status === 'approved';
    }
}
